import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getQueryDataForType, getTableConfig } from '../selectors/reports';
import { getFilters } from '../selectors/fields';
import { getGridFilteredData } from '../utils/grids';
import { useAsync } from '../utils/customHooks';
import { IN_OPERATOR } from '../utils/reportsConsts';
import { queryTypeToAPI } from './apiMapping';
import { expandMetadata, expandRow } from '../utils/reports';
import { ANONYMOUS_REPORT_TYPE } from '../selectors/reportsConfig/anonymous';
import { isObjectEmpty } from '../utils/general';
import { getPostProcessFilter, getValuesForQueryFilter } from './filterUtils';
import { DATE_FIELDS } from '../components/pages/reports/consts';

export function gridFilterModelToAPI(model, filters = [], columnDefs) {
    const postProcessFilters = [];
    const queryFilters = [];

    const dimensionValuesMap = filters.reduce((acc, { name, values }) => {
        acc[name] = values;
        return acc;
    }, {});

    const filtersArray = Object.entries(model).map(([field, config]) => ({ field, ...config }));

    for (const filter of filtersArray) {
        const { field } = filter;
        const def = columnDefs.find(c => c.field === field);

        const isDimension = field in dimensionValuesMap;

        if (isDimension) {
            if (filter.filterType === 'set') {
                queryFilters.push({ dimension: field, operator: IN_OPERATOR, values: filter.values });
                continue;
            }

            const matchedValues = getValuesForQueryFilter(filter, dimensionValuesMap, def);
            if (!matchedValues.length) return null;

            queryFilters.push({ dimension: field, operator: IN_OPERATOR, values: matchedValues });
        } else if (filter.filterType === 'set') {
            postProcessFilters.push({ field, operator: IN_OPERATOR, value: filter.values });
        } else {
            postProcessFilters.push(getPostProcessFilter(filter, def));
        }
    }

    return {
        post_process_filters: postProcessFilters.length ? postProcessFilters : undefined,
        filters: queryFilters,
    };
}

// --------------------
// Hook: useTotalsFetching
// --------------------

export const useFetchTotals = (reportType, gridId, queryObject) => {
    const cancelRequestRef = useRef(null);
    const [response, runAsync] = useAsync();

    const queryData = useSelector(state => queryObject || getQueryDataForType(reportType)(state));
    const tableConfig = useSelector(state => getTableConfig(reportType)(state));
    const secretId = useSelector(state => state.anonymous?.secretId);
    const password = useSelector(state => state.anonymous?.password);
    const initialFilters = useSelector(getFilters);
    const isAnonymousReport = reportType === ANONYMOUS_REPORT_TYPE;

    const runFetchTotals = useCallback(
        async (colDefs, filterModel = {}) => {
            if (isObjectEmpty(queryData)) return null;

            if (cancelRequestRef.current) {
                cancelRequestRef.current.abort();
            }

            cancelRequestRef.current = new AbortController();

            const filteredRows = getGridFilteredData(gridId);
            if (!filteredRows?.length) return null;

            let payload = {
                ...(isAnonymousReport ? tableConfig.request : queryData),
                is_totals: true,
            };

            if (Object.keys(filterModel).length > 0) {
                const translated = gridFilterModelToAPI(filterModel, initialFilters, colDefs);
                if (!translated) return null;

                const translatedDimensions = new Set(translated.filters.map(f => f.dimension));
                const baseFilters = payload.filters?.filter(f => !translatedDimensions.has(f.dimension)) || [];

                payload = {
                    ...payload,
                    ...translated,
                    filters: [...baseFilters, ...translated.filters],
                };
            }

            const postProcessFields = payload.post_process_filters?.map(({ field }) => field) || [];
            const allDateFields = DATE_FIELDS;

            if (!postProcessFields.some(field => allDateFields.includes(field))) {
                payload.time_breakdown = ['all'];
            }

            const options = {
                channel: 'TOTALS',
                signal: cancelRequestRef.current.signal,
                unique: false,
            };

            try {
                if (isAnonymousReport)
                    return await queryTypeToAPI.reports.runAnonymousQuery(secretId, password, payload, options);

                return await queryTypeToAPI[reportType].runQuery(payload, options);
            } catch (error) {
                if (error.name !== 'AbortError') throw error;
            }

            return null;
        },
        [reportType, queryData, initialFilters, secretId, password, gridId, tableConfig, isAnonymousReport]
    );

    const fetchTotals = useCallback((colDefs, filters) => runAsync(runFetchTotals(colDefs, filters)), [
        runAsync,
        runFetchTotals,
        queryData,
    ]);

    const responseData = response?.data;
    const responseMetadata = responseData?.metadata;
    const responseTotal = responseData?.total;
    const tableMetadata = expandMetadata(responseMetadata, queryData?.compare);
    const totalsData = responseTotal ? expandRow(tableMetadata, responseTotal) : null;

    return {
        totalsData,
        isLoading: response.isLoading,
        fetchTotals,
    };
};
